import { useEffect, useRef, useState } from "react";
import { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Send, MessageSquareText, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const PROMPTS = [
  { id: "style", text: "Style advice for a formal gala this weekend", testId: "ai-prompt-pill-style" },
  { id: "fitness", text: "10-minute hip & shoulder mobility routine", testId: "ai-prompt-pill-fitness" },
  { id: "nutrition", text: "High-protein meal plan on the go", testId: "ai-prompt-pill-nutrition" },
  { id: "etiquette", text: "Dining etiquette for a first business meeting", testId: "ai-prompt-pill-etiquette" },
];

export default function AiChat() {
  const { user } = useAuth();
  const authed = user && typeof user === "object";
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!authed) return;
    fetch(`${API}/ai/messages`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setMessages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [authed]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    if (!text.trim() || streaming || !authed) return;
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text }),
      });
      if (!res.ok || !res.body) {
        throw new Error("Chat failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6);
          if (payload === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            if (last?.role === "assistant") {
              copy[copy.length - 1] = { ...last, content: last.content + payload };
            }
            return copy;
          });
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last?.role === "assistant" && !last.content) {
          copy[copy.length - 1] = { ...last, content: "Dev is momentarily off the line. Try again in a sec." };
        }
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <section id="ai-chat" className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-4">
            <div className="eyebrow">AI Consultant</div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase mt-2 leading-tight">
              Ask <span className="gold-text">Dev Da Don AI</span>
            </h2>
            <p className="text-[var(--text-secondary)] mt-3">
              Instant, unfiltered advice on fitness, style, nutrition, and etiquette — trained on Dev&apos;s voice.
            </p>
            <div className="mt-6 space-y-2">
              {PROMPTS.map((p) => (
                <button
                  key={p.id}
                  data-testid={p.testId}
                  onClick={() => send(p.text)}
                  disabled={!authed || streaming}
                  className="w-full text-left px-4 py-3 rounded-xl border border-[var(--border-subtle)] hover:border-[var(--border-gold)] hover:text-[var(--gold)] transition-colors text-sm text-[var(--text-secondary)] disabled:opacity-40"
                >
                  <Sparkles size={12} className="inline mr-2 text-[var(--gold)]" />
                  {p.text}
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-8">
            <div
              data-testid="ai-consultant-chat-container"
              className="gr-card p-0 overflow-hidden flex flex-col h-[560px]"
            >
              <div className="px-5 py-4 border-b border-[var(--border-subtle)] flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[var(--gold)] to-[#8a6b12] grid place-items-center text-[#0a0a0e]">
                  <MessageSquareText size={16} />
                </div>
                <div>
                  <div className="font-semibold text-sm">Dev Da Don AI</div>
                  <div className="text-[10px] uppercase tracking-widest text-[var(--gold)]">Live · Claude Sonnet 5</div>
                </div>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
                {messages.length === 0 && (
                  <div className="text-center text-[var(--text-muted)] text-sm py-20">
                    {authed
                      ? "Ask anything — style, mobility, nutrition, presence."
                      : "Sign in to start chatting with Dev Da Don AI."}

                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${
                        m.role === "user" ? "chat-bubble-user" : "chat-bubble-ai"
                      } ${streaming && i === messages.length - 1 && m.role === "assistant" ? "gr-cursor" : ""}`}
                    >
                      {m.content || (m.role === "assistant" ? "…" : "")}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-[var(--border-subtle)] p-3 flex gap-2">
                <input
                  data-testid="ai-chat-message-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send(input)}
                  placeholder={authed ? "Message Dev Da Don…" : "Sign in to send"}
                  disabled={!authed || streaming}
                  className="flex-1 bg-[var(--bg-elevated)] border border-[var(--border-subtle)] focus:border-[var(--border-gold)] outline-none rounded-full px-4 py-2.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]"
                />
                {authed ? (
                  <button
                    data-testid="ai-chat-send-button"
                    onClick={() => send(input)}
                    disabled={streaming || !input.trim()}
                    className="gr-btn-gold rounded-full px-4 py-2.5 disabled:opacity-50"
                  >
                    <Send size={16} />
                  </button>
                ) : (
                  <Link
                    to="/login"
                    data-testid="ai-chat-signin-link"
                    className="gr-btn-gold rounded-full px-5 py-2.5 text-sm"
                  >
                    Sign in
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
